/**
 * CompositeFieldWidget — editor for a COMPOSITE_REF attribute: a value that
 * can be EITHER a reference into one of several catalogs OR a raw primitive
 * (string/number/date/boolean), discriminated by a declared `{name}_type`
 * sibling attribute (props.composite_type_of — see `core/designer/composite.py`).
 *
 * Modelled on RefFieldWidget's Autocomplete + TTL-cache idiom, extended to
 * search MULTIPLE ref targets in parallel (Promise.allSettled — one failed
 * target never breaks the others) and to offer parse-gated primitive
 * candidate rows alongside the ref groups.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAppLocale } from '@/app/permissions/useAppLocale'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import apiClient from '@/utils/api'
import type { AttributeDef } from '../designer/services/configObjectApi'
import type { RefLookupOption } from './RefFieldWidget'
import { resolveLabel } from './resolveLabel'
import { createTtlCache } from './ttlCache'
import { useDebouncedValue } from './useDebouncedValue'

export interface CompositeFieldWidgetProps {
  attr: AttributeDef // props.composite_types drives groups
  value: unknown // the value key
  typeValue: unknown // the `${name}_type` key
  onChangePair: (value: unknown, typeName: string | null) => void
  connectionId: string
  disabled?: boolean
  compact?: boolean // TabularGrid cell mode
  onOpenRef?: (target: string, id: string) => void
}

// --- Pure helpers (exported for direct unit testing) -----------------------

export type CompositeRefOption = {
  kind: 'ref'
  id: string
  display: string
  synonyms?: Record<string, string>
  target: string
}
export type CompositePrimitiveType = 'string' | 'number' | 'date' | 'boolean'
/** MUI Autocomplete skips selectNewValue on Enter when this flag is set. */
type MuiPreventableKeyboardEvent = React.KeyboardEvent & { defaultMuiPrevented?: boolean }

export type CompositePrimitiveOption = {
  kind: 'primitive'
  type: CompositePrimitiveType
  value: string
  label: string
}
export type CompositeOption = CompositeRefOption | CompositePrimitiveOption

export const PRIMITIVE_GROUP_LABEL = 'Value'

const PRIMITIVE_LITERALS = new Set<CompositePrimitiveType>(['string', 'number', 'date', 'boolean'])

/** Ordered ref targets + the set of primitive literals in a composite's type list. */
export function splitCompositeTypes(types: unknown): { refTargets: string[]; primitives: Set<CompositePrimitiveType> } {
  const list = Array.isArray(types) ? types.map((t) => String(t)) : []
  const refTargets: string[] = []
  const primitives = new Set<CompositePrimitiveType>()
  for (const t of list) {
    if (PRIMITIVE_LITERALS.has(t as CompositePrimitiveType)) {
      primitives.add(t as CompositePrimitiveType)
    } else if (t) {
      refTargets.push(t)
    }
  }
  return { refTargets, primitives }
}

function typeLabelPrefix(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

function isTrueish(raw: unknown): boolean {
  return raw === true || raw === 1 || raw === 'true' || raw === '1'
}

/** Yes/No for booleans; raw text for string; "<Type>: <raw>" for number/date. */
export function primitiveDisplayLabel(type: string, raw: unknown): string {
  if (type === 'boolean') return isTrueish(raw) ? 'Yes' : 'No'
  if (type === 'string') return String(raw ?? '')
  return `${typeLabelPrefix(type)}: ${raw ?? ''}`
}

function isValidDateCandidate(text: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return false
  return !Number.isNaN(new Date(text).getTime())
}

/**
 * Primitive candidate rows for the currently typed text: string is a blur/Enter
 * fallback, not a candidate row. Number only when it parses as finite, date only
 * when it parses as a valid ISO date, boolean (Yes/No) whenever 'boolean' is in
 * the set at all (not text-derived — MUI's own option filter narrows it like any
 * other option).
 */
export function buildPrimitiveCandidates(
  primitives: Set<CompositePrimitiveType>,
  text: string,
): CompositePrimitiveOption[] {
  const out: CompositePrimitiveOption[] = []
  const trimmed = text.trim()
  if (trimmed) {
    if (primitives.has('number') && Number.isFinite(Number(trimmed))) {
      out.push({ kind: 'primitive', type: 'number', value: trimmed, label: `Number: ${trimmed}` })
    }
    if (primitives.has('date') && isValidDateCandidate(trimmed)) {
      out.push({ kind: 'primitive', type: 'date', value: trimmed, label: `Date: ${trimmed}` })
    }
  }
  if (primitives.has('boolean')) {
    out.push({ kind: 'primitive', type: 'boolean', value: 'true', label: 'Yes' })
    out.push({ kind: 'primitive', type: 'boolean', value: 'false', label: 'No' })
  }
  return out
}

/**
 * Decide the pair for leftover typed text. `null` = caller must not touch
 * onChangePair (string is not in the set). Empty-after-trim clears.
 * Non-empty commits `text` as typed (leading/trailing spaces kept).
 */
export function commitTypedString(
  primitives: Set<CompositePrimitiveType>,
  text: string,
): { value: unknown; typeName: string | null } | null {
  if (!primitives.has('string')) return null
  if (text.trim() === '') return { value: null, typeName: null }
  return { value: text, typeName: 'string' }
}

/** Flattens per-target lookup results into ref options, in SET order — the
 * order MUI's `groupBy` needs (it groups consecutive same-group options). */
export function buildRefOptions(
  refTargets: string[],
  resultsByTarget: Record<string, RefLookupOption[]>,
): CompositeRefOption[] {
  const out: CompositeRefOption[] = []
  for (const target of refTargets) {
    for (const o of resultsByTarget[target] ?? []) {
      out.push({ kind: 'ref', id: o.id, display: o.display, synonyms: o.synonyms, target })
    }
  }
  return out
}

export function sameCompositeOption(a: CompositeOption, b: CompositeOption): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'ref' && b.kind === 'ref') return a.id === b.id && a.target === b.target
  if (a.kind === 'primitive' && b.kind === 'primitive') return a.type === b.type && a.value === b.value
  return false
}

function optionLabel(o: CompositeOption, locale: string | null, defaultCode?: string): string {
  if (o.kind === 'ref') return resolveLabel({ synonyms: o.synonyms, title: o.display, name: o.id }, locale, defaultCode)
  return o.label
}

// --- Component ---------------------------------------------------------

const LOOKUP_TTL_MS = 30_000
const lookupCache = createTtlCache<RefLookupOption[]>(LOOKUP_TTL_MS)

/** Test-only escape hatch — mirrors `invalidateRefLookupCache` in RefFieldWidget. */
export function invalidateCompositeLookupCache(key?: string): void {
  lookupCache.invalidate(key)
}

export function CompositeFieldWidget({
  attr,
  value,
  typeValue,
  onChangePair,
  connectionId,
  disabled,
  compact,
  onOpenRef,
}: CompositeFieldWidgetProps): React.JSX.Element {
  const { locale, defaultCode } = useAppLocale()

  const { refTargets, primitives } = useMemo(
    () => splitCompositeTypes(attr.props?.composite_types),
    // Array identity of composite_types may change per parent render even
    // when its contents don't — key on the joined contents instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(attr.props?.composite_types) ? attr.props.composite_types.join('|') : ''],
  )

  const [inputValue, setInputValue] = useState('')
  // Search is a separate channel from the input. Syncing the selected
  // presentation (or the raw id fallback) into inputValue used to re-query
  // /lookup?search=<label-or-id>, empty the groups, and leave the hex id
  // in the box — the same class RefFieldWidget already closed.
  const [searchText, setSearchText] = useState('')
  const debouncedQuery = useDebouncedValue(searchText, 250)
  const [groups, setGroups] = useState<Record<string, RefLookupOption[]>>({})
  const [groupsReady, setGroupsReady] = useState(false)
  const [resolvedRef, setResolvedRef] = useState<RefLookupOption | null>(null)
  const resolvedLocaleRef = useRef<string | null>(null)
  // True once the user has typed since the last committed/external value —
  // guards the resolved-label sync effect below from clobbering an in-flight
  // search every time a lookup response updates unrelated groups.
  const editingRef = useRef(false)
  // Keeps raw typed string visible after blur/Enter commit until parent props catch up.
  const committedStringRef = useRef<string | null>(null)

  useEffect(() => {
    editingRef.current = false
    committedStringRef.current = null
  }, [value, typeValue])

  useEffect(() => {
    if (!connectionId || refTargets.length === 0) {
      setGroups({})
      setGroupsReady(true)
      return
    }
    let cancelled = false
    setGroupsReady(false)
    const q = debouncedQuery.trim()
    Promise.allSettled(
      refTargets.map((target) =>
        lookupCache.get(`${connectionId}|${target}|${locale ?? ''}|${q.toLowerCase()}`, () =>
          apiClient
            .get(
              `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(target)}/lookup` +
                `?search=${encodeURIComponent(q)}&locale=${encodeURIComponent(locale ?? '')}`,
            )
            .then((res: any) => {
              const rows = res?.data?.data || []
              return Array.isArray(rows) ? (rows as RefLookupOption[]) : []
            }),
        ),
      ),
    ).then((settled) => {
      if (cancelled) return
      const next: Record<string, RefLookupOption[]> = {}
      settled.forEach((res, i) => {
        // A rejected target contributes an empty (silently missing) group —
        // never breaks the others.
        next[refTargets[i]] = res.status === 'fulfilled' ? res.value : []
      })
      setGroups(next)
      setGroupsReady(true)
    })
    return () => {
      cancelled = true
    }
    // refTargets is derived fresh each render from a memoized primitive key —
    // safe to include by reference here since it's stable across re-renders
    // with the same composite_types contents.
  }, [refTargets, connectionId, locale, debouncedQuery])

  // Stored ref may sit outside the browse window or the active search.
  // Resolve it by id so the field never renders the raw hex.
  useEffect(() => {
    const t = typeValue == null || typeValue === '' ? '' : String(typeValue)
    if (!connectionId || !t || !refTargets.includes(t) || value == null || value === '') {
      setResolvedRef(null)
      resolvedLocaleRef.current = null
      return
    }
    if (!groupsReady) return
    if ((groups[t] ?? []).some((o) => o.id === value)) {
      setResolvedRef(null)
      return
    }
    if (resolvedRef?.id === value && resolvedLocaleRef.current === (locale ?? '')) return
    let cancelled = false
    lookupCache
      .get(`${connectionId}|${t}|${locale ?? ''}|id:${String(value)}`, () =>
        apiClient
          .get(
            `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(t)}/lookup` +
              `?locale=${encodeURIComponent(locale ?? '')}&ids=${encodeURIComponent(String(value))}`,
          )
          .then((res: any) => {
            const rows = res?.data?.data || []
            return Array.isArray(rows) ? (rows as RefLookupOption[]) : []
          }),
      )
      .then((rows) => {
        if (cancelled) return
        resolvedLocaleRef.current = locale ?? ''
        setResolvedRef(rows[0] ?? null)
      })
      .catch(() => {
        if (!cancelled) setResolvedRef(null)
      })
    return () => {
      cancelled = true
    }
    // resolvedRef is a skip-guard only — including it would re-run on every set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, locale, value, typeValue, groups, groupsReady, refTargets])

  const selectedOption: CompositeOption | null = useMemo(() => {
    if (value == null || value === '' || typeValue == null || typeValue === '') return null
    const t = String(typeValue)
    if (refTargets.includes(t)) {
      const match = (groups[t] ?? []).find((o) => o.id === value)
      const resolved = resolvedRef && resolvedRef.id === value ? resolvedRef : null
      const row = match ?? resolved
      return {
        kind: 'ref',
        id: String(value),
        display: row?.display ?? String(value),
        synonyms: row?.synonyms,
        target: t,
      }
    }
    return { kind: 'primitive', type: t as CompositePrimitiveType, value: String(value), label: primitiveDisplayLabel(t, value) }
  }, [value, typeValue, groups, refTargets, resolvedRef])

  const resolvedLabel = selectedOption ? optionLabel(selectedOption, locale, defaultCode) : ''
  useEffect(() => {
    if (editingRef.current) return
    if (committedStringRef.current != null && (value == null || value === '')) {
      setInputValue(committedStringRef.current)
      return
    }
    setInputValue(resolvedLabel)
  }, [resolvedLabel, value])

  const options: CompositeOption[] = useMemo(() => {
    const refs = buildRefOptions(refTargets, groups)
    const selectedRef = selectedOption?.kind === 'ref' ? selectedOption : null
    const withSelected =
      selectedRef && !refs.some((o) => o.id === selectedRef.id && o.target === selectedRef.target)
        ? [selectedRef, ...refs]
        : refs
    return [...withSelected, ...buildPrimitiveCandidates(primitives, inputValue)]
  }, [refTargets, groups, primitives, inputValue, selectedOption])

  const [listOpen, setListOpen] = useState(false)

  const applyTypedString = (text = inputValue) => {
    if (!editingRef.current) return
    const result = commitTypedString(primitives, text)
    editingRef.current = false
    if (result === null) return
    if (result.typeName === 'string' && result.value != null) {
      const raw = String(result.value)
      committedStringRef.current = raw
      setInputValue(raw)
    } else {
      committedStringRef.current = null
    }
    onChangePair(result.value, result.typeName)
  }

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      open={listOpen}
      onOpen={() => setListOpen(true)}
      onClose={(_e, reason) => {
        setListOpen(false)
        if (reason !== 'blur') return
        const textAtBlur = inputValue
        // Click-to-select fires onChange (clears editingRef) in the same
        // turn; commit only if that did not happen.
        queueMicrotask(() => applyTypedString(textAtBlur))
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        ;(e as MuiPreventableKeyboardEvent).defaultMuiPrevented = true
        applyTypedString()
        setListOpen(false)
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === 'reset') {
          if (committedStringRef.current != null) return
          setInputValue(v)
          return
        }
        if (reason === 'clear') {
          editingRef.current = true
          setInputValue(v)
          setSearchText('')
          return
        }
        if (reason === 'input') {
          editingRef.current = true
          setInputValue(v)
          setSearchText(v)
        }
      }}
      onChange={(_e, opt) => {
        editingRef.current = false
        if (!opt) {
          setSearchText('')
          onChangePair(null, null)
          return
        }
        if (opt.kind === 'ref') onChangePair(opt.id, opt.target)
        else onChangePair(opt.value, opt.type)
      }}
      getOptionLabel={(o) => optionLabel(o, locale, defaultCode)}
      isOptionEqualToValue={(o, v) => sameCompositeOption(o, v)}
      filterOptions={(opts) => opts}
      groupBy={(o) => (o.kind === 'ref' ? o.target : PRIMITIVE_GROUP_LABEL)}
      disabled={disabled}
      renderOption={(props, option) => (
        <li {...props} key={option.kind === 'ref' ? `ref:${option.target}:${option.id}` : `p:${option.type}:${option.value}`}>
          {optionLabel(option, locale, defaultCode)}
        </li>
      )}
      renderInput={(params) => {
        const openRefButton =
          selectedOption?.kind === 'ref' && onOpenRef ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onOpenRef(selectedOption.target, selectedOption.id)
              }}
              aria-label="Open referenced record"
              sx={{ color: 'text.secondary' }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          ) : null
        const typeHintChip =
          selectedOption?.kind === 'primitive' ? (
            <Chip size="small" label={selectedOption.type} sx={{ mr: 0.5 }} />
          ) : null

        return (
          <TextField
            {...params}
            variant={compact ? 'standard' : 'outlined'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {typeHintChip}
                  {openRefButton}
                  {params.InputProps?.endAdornment}
                </>
              ),
            }}
          />
        )
      }}
    />
  )
}
